import React, { useState } from 'react';
import classnames from 'classnames';
import { getDomain } from '@bizzabo/whitelabel-utils';
import { ChatWebIcon, CloseIcon } from '@bizzabo/icons';
import { VimeoWeb as VimeoWebCommon, VimeoChat as VimeoChatCommon, VimeoMobileApp as VimeoMobileAppCommon } from '@bizzabo/broadcast';
import FeatureToggleUtils from 'FeatureToggleUtils';

export const getVimeoVideoNumericalId = videoId => {
    if (Number.isInteger(+videoId)) {
        return videoId;
    }

    if (getDomain(videoId) === 'vimeo.com') {
        return videoId.match(/\d+/)[0];
    }

    return null;
};

export const getVimeoLinksForEmbedding = videoId => {
    const numericalId = getVimeoVideoNumericalId(videoId);
    const isRecurring = videoId.includes?.('event');
    const mobileQueryParams = 'pip=true&playsinline=true&responsive=true';

    return isRecurring
        ? {
            eventLink: `https://vimeo.com/event/${numericalId}/embed?autoplay=1&${mobileQueryParams}`,
            chatLink: `https://vimeo.com/event/${numericalId}/chat/`
        }
        : {
            eventLink: `https://player.vimeo.com/video/${numericalId}?autoplay=1&${mobileQueryParams}`,
            chatLink: `https://vimeo.com/live-chat/${numericalId}/`
        };
};

const VimeoMobileApp = (
    {
        eventLink,
        hasChat,
        videoIdOrLink
    }
) => {
    const [isMobileAppChatShown, toggleChat] = useState(false);

    return (
        <div className="vimeo-wrapper">
            <div className={classnames('toggle-wrapper', { 'shown': isMobileAppChatShown })} onClick={() => toggleChat(!isMobileAppChatShown)}>
                {!isMobileAppChatShown && <ChatWebIcon fill='#fff' fontSize={32} />}
                {isMobileAppChatShown && <CloseIcon fill='#fff' fontSize={22} />}
            </div>
            <iframe
                className="vimeo-video"
                src={eventLink}
                frameBorder="0"
                allowFullScreen
                allow="autoplay; encrypted-media"
            />
            { hasChat && <VimeoChat videoId={videoIdOrLink} isMobileApp isMobileAppChatShown={isMobileAppChatShown} />}
        </div>
    );
};

const VimeoWeb = (
    {
        wrapperStyle,
        iframeWrapperStyle,
        isIframeLoading,
        eventLink,
        vedeoIframeStyle,
        setIsIframeLoading,
        children
    }
) => (
    <div className="vimeo-wrapper" style={wrapperStyle}>
        <div style={iframeWrapperStyle}>
            {isIframeLoading && <i className="iframe-broadcast-spinner fa fa-spinner fa-spin fa-2x"/>}
            <iframe
                src={eventLink}
                frameBorder="0"
                allowFullScreen
                style={vedeoIframeStyle}
                onLoad={() => setIsIframeLoading(false)}
                allow="autoplay; encrypted-media"
            />
        </div>
        {children}
    </div>
);

export const Vimeo = (
    {
        videoId: videoIdOrLink,
        isFullWidth,
        showOwnChat,
        isIframeLoading,
        setIsIframeLoading,
        isMobileApp
    }
) => {
    const isVimeoCommonPlayerEnabled = FeatureToggleUtils.isFeatureEnabled('broadcastPlayerVimeo');

    if (isVimeoCommonPlayerEnabled) {
        return isMobileApp ?
            (
                <VimeoMobileAppCommon
                    videoIdOrLink={videoIdOrLink}
                    hasChat={showOwnChat}
                    chatWebIcon={<ChatWebIcon fill='#fff' fontSize={32} />}
                    closeIcon={<CloseIcon fill='#fff' fontSize={22} />}
                />
            ) :
            (
                <VimeoWebCommon
                    videoIdOrLink={videoIdOrLink}
                    isIframeLoading={isIframeLoading}
                    onLoad={setIsIframeLoading}
                    isFullWidth={isFullWidth}
                    loadingIconHtml={<i className="iframe-broadcast-spinner fa fa-spinner fa-spin fa-2x"/>}
                >
                    {
                        showOwnChat &&
                        <VimeoChatCommon
                            videoIdOrLink={videoIdOrLink}
                            isFullWidth={isFullWidth}
                            isMobileApp={isMobileApp}
                        />
                    }
                </VimeoWebCommon>
            );
    }
    const { eventLink } = getVimeoLinksForEmbedding(videoIdOrLink);

    const wrapperStyle = {
        display: 'flex',
        flexDirection: isFullWidth ? 'column' : 'row',
        alignContent: 'stretch',
        width: '100%',
    };

    const iframeWrapperStyle = {
        flex: 2,
        padding: isFullWidth ? '57% 0 0 0' : '38% 0 0 0',
        position: 'relative',
    };

    const vedeoIframeStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%'
    };

    return isMobileApp ?
        (
            <VimeoMobileApp
                hasChat={showOwnChat}
                eventLink={eventLink}
                videoIdOrLink={videoIdOrLink}
            />
        ) :
        (
            <VimeoWeb
                wrapperStyle={wrapperStyle}
                iframeWrapperStyle={iframeWrapperStyle}
                isIframeLoading={isIframeLoading}
                eventLink={eventLink}
                vedeoIframeStyle={vedeoIframeStyle}
                setIsIframeLoading={setIsIframeLoading}
            >
                {
                    showOwnChat && <VimeoChat
                        videoId={videoIdOrLink}
                        isFullWidth={isFullWidth}
                        isMobileApp={isMobileApp}
                    />
                }
            </VimeoWeb>
        );
};

export const VimeoChat = (
    {
        videoId: videoIdOrLink,
        isFullWidth = false,
        isMobileApp,
        isMobileAppChatShown = false
    }
) => {
    const { chatLink } = getVimeoLinksForEmbedding(videoIdOrLink);

    const chatWrapperStyle = {
        flex: 1,
        padding: isFullWidth ? '290px 0 0 0' : '19% 0 0 0',
        position: 'relative',
    };

    const chatIframeStyle = {
        position: 'absolute',
        padding: '0',
        top: 0,
        right: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        borderLeft: 'none',
        borderBottom: 'none',
        borderTop: '1px solid #979797',
        borderRight: isFullWidth ? 'none' : '1px solid #979797',
    };

    return isMobileApp ?
        (
            <iframe className={classnames('vimeo-chat', { 'shown': isMobileAppChatShown })} src={chatLink} />
        ) :
        (
            <div style={chatWrapperStyle}>
                <iframe src={chatLink} style={chatIframeStyle}/>
            </div>
        );
};
